import type { LandingIconName } from './icons';

export type LoopZoneKey = 'org' | 'phy' | 'pat' | 'sys';

export type LoopZone = Readonly<{
  key: LoopZoneKey;
  name: string;
  role: string;
  color: string;
}>;

export type LoopStep = Readonly<{
  icon: LandingIconName;
  zone: LoopZoneKey;
  label: string;
  tag: string;
  note: string;
  stat: string;
  statLabel: string;
}>;

export const LOOP_ZONES = {
  org: { key: 'org', name: 'Labs', role: 'Sources & produces', color: 'oklch(0.79 0.105 211)' },
  phy: { key: 'phy', name: 'Physician', role: 'Purchases & administers', color: 'oklch(0.75 0.12 262)' },
  pat: { key: 'pat', name: 'Patient', role: 'Generates the signal', color: 'oklch(0.72 0.13 300)' },
  sys: { key: 'sys', name: 'Patient/System', role: 'Feeds the loop back', color: 'oklch(0.72 0.13 300)' },
} as const satisfies Record<LoopZoneKey, LoopZone>;

export const LOOP_STEPS = [
  {
    icon: 'procure',
    zone: 'org',
    label: 'Source',
    tag: 'Source & custody',
    note: 'Stem cell lines are sourced and logged with full chain-of-custody from their point of origin.',
    stat: '100%',
    statLabel: 'chain-of-custody',
  },
  {
    icon: 'produce',
    zone: 'org',
    label: 'Produce',
    tag: 'Manufacture & release',
    note: 'Each line is processed, characterized, and released as a lot-numbered, traceable product.',
    stat: '1,204',
    statLabel: 'lots released',
  },
  {
    icon: 'purchase',
    zone: 'phy',
    label: 'Acquire',
    tag: 'Clinical acquisition',
    note: 'Physicians order the product they need — every unit tied back to the exact line it came from.',
    stat: '390',
    statLabel: 'clinical sites',
  },
  {
    icon: 'administer',
    zone: 'phy',
    label: 'Administer',
    tag: 'Point of care · research',
    note: 'Clinicians administer the therapy under a versioned research protocol and log every dose given.',
    stat: '1,820',
    statLabel: 'procedures logged',
  },
  {
    icon: 'outcomes',
    zone: 'pat',
    label: 'Measure',
    tag: 'Measured response',
    note: 'Standardized endpoints capture how each patient actually responds — over weeks and months.',
    stat: '6 mo',
    statLabel: 'median follow-up',
  },
  {
    icon: 'feedback',
    zone: 'sys',
    label: 'Learn',
    tag: 'Closes the loop',
    note: 'Every outcome teaches the platform — sharpening the next line procured, produced, and prescribed for every party.',
    stat: '+18%',
    statLabel: 'response lift',
  },
] as const satisfies readonly LoopStep[];
